/**
 * The texted-card offer (piece 20; decisions 0202 and 5180). An inbound text
 * carrying a vCard raises one `TextedCardOffer` per card, which the Leads page
 * draws as a row reading "Texted card from" the number, with Create Lead,
 * Create Person at a Company and Dismiss. Each create opens the existing form
 * pre-filled from the card. 🔨 The offer keeps the card's own fields and
 * nothing else: the name, the company, the title, the phone lines and the
 * email addresses. A postal address, a photograph, a note or a link on the
 * card is never stored, executed or rendered.
 */
import type { EditField } from '../pages/recordPages';
import { formatPhone } from '../records/phone';
import type { VCardContact, VCardEmail, VCardPhone } from '../records/vcard';

/** The offers on the Leads page, open ones only, newest first. 🔨 Relative to the API root. */
export const TEXTED_CARDS_PATH = '/leads/texted-cards';
/** Closes the offer with nothing created. */
export const textedCardDismissPath = (id: string): string => `${TEXTED_CARDS_PATH}/${id}/dismiss`;

/** The ChecklistItem kind that carries the offer to the dispatch head's To-Do list, and the source it names. */
export const TEXTED_CARD_OFFER_KIND = 'TextedCardOffer' as const;
export const TEXTED_CARD_SOURCE = 'TextedCardOffer';

/** The longest a stored card field may be; a longer value is cut, never refused, because the text is already filed. */
export const TEXTED_CARD_FIELD_MAX = 200;
/** The most phone lines and the most addresses kept from one card. */
export const TEXTED_CARD_LINES_MAX = 5;
/**
 * 🔨 The most cards one text makes offers of, and the most vCard attachments the carrier's adapter reads from
 * one text (decision 5180). A card past it is dropped and counted in one log line, never stored, so a single
 * text cannot flood the dispatch head's To-Do list or hold the carrier's request open.
 */
export const TEXTED_CARDS_PER_TEXT = 3;

/** A card as the offer stores it: its own fields and nothing else (decision 0202). */
export interface TextedCard {
  fullName: string;
  firstName: string;
  lastName: string;
  organization: string | null;
  title: string | null;
  phones: VCardPhone[];
  emails: VCardEmail[];
}

/** The two acts an offer takes a record from. */
export type TextedCardRecordType = 'Lead' | 'PersonRecord';

/** One open offer as the Leads page reads it. */
export interface TextedCardOfferRow {
  id: string;
  /** The sending number, E.164. */
  fromNumber: string;
  /** When the text arrived, an instant. */
  receivedAt: string;
  card: TextedCard;
  /**
   * The company Create Person at a Company opens on: the one the sending number already
   * works at, else the one company whose name is the card's company exactly; null where
   * neither names exactly one.
   */
  company: { id: string; displayName: string } | null;
}

export interface TextedCardsAnswer {
  rows: TextedCardOfferRow[];
  /** Whether the viewer may take or dismiss an offer: write on the Deal module, the Lead's. */
  mayAct: boolean;
}

/**
 * A value as stored: control characters and the line and paragraph separators read as spaces, the bidi controls (U+061C, U+200E, U+200F, U+202A to U+202E, U+2066 to U+2069) and the invisible spaces (U+200B, U+2060, U+FEFF) are
 * dropped so none can reorder or hide a name on the Leads page, while a zero-width non-joiner and a soft hyphen stay (T2726), then trimmed and cut to the cap (T2712).
 */
function cut(value: string | null | undefined): string {
  const plain = (value ?? '').replace(/[\p{Cc}\u2028\u2029]/gu, ' ').replace(/[\u202A-\u202E\u2066-\u2069\u200E\u200F\u061C\u200B\u2060\uFEFF]/gu, '');
  return plain.trim().slice(0, TEXTED_CARD_FIELD_MAX);
}

/** The card's own fields, cut to their caps; the version, the addresses and every other property are dropped here. */
export function textedCardOf(contact: VCardContact): TextedCard {
  return {
    fullName: cut(contact.fullName),
    firstName: cut(contact.firstName),
    lastName: cut(contact.lastName),
    organization: cut(contact.organization) || null,
    title: cut(contact.title) || null,
    phones: contact.phones.slice(0, TEXTED_CARD_LINES_MAX).map((phone) => ({ kind: phone.kind, value: cut(phone.value), extension: cut(phone.extension) || null })).filter((phone) => phone.value !== ''),
    emails: contact.emails.slice(0, TEXTED_CARD_LINES_MAX).map((email) => ({ kind: email.kind, value: cut(email.value) })).filter((email) => email.value !== ''),
  };
}

/** Whether a card names anybody: a card with no name, no company, no phone and no email offers nothing. */
export function textedCardNamesAnybody(card: TextedCard): boolean {
  return Boolean(card.fullName || card.firstName || card.lastName || card.organization || card.phones.length > 0 || card.emails.length > 0);
}

/** The name the card gives, as a person reads it: the formatted name, else the two parts. */
export function textedCardName(card: TextedCard): string {
  return card.fullName || [card.firstName, card.lastName].filter(Boolean).join(' ');
}

/** The row's own words: Texted card from (217) 555-0100. */
export function textedCardFrom(fromNumber: string): string {
  return formatPhone(fromNumber) || fromNumber;
}

/** The card's line under the row's words: the name, the title and the company, each where the card gives one. */
export function textedCardSummary(card: TextedCard): string {
  return [textedCardName(card), card.title, card.organization, card.phones[0] ? formatPhone(card.phones[0].value, card.phones[0].extension) : null, card.emails[0]?.value ?? null].filter((part): part is string => Boolean(part)).join(' · ');
}

/** The name split as the Person form asks it: the card's own parts, else the last word of the formatted name as the last name. */
export function textedCardNameParts(card: TextedCard): { firstName: string; lastName: string } {
  if (card.firstName || card.lastName) return { firstName: card.firstName, lastName: card.lastName };
  const words = card.fullName.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return { firstName: words[0] ?? '', lastName: '' };
  return { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1]! };
}

/** The work email the Person form opens on: the card's work address, else its first. */
function cardEmail(card: TextedCard): string {
  return (card.emails.find((email) => email.kind === 'WorkEmail') ?? card.emails[0])?.value ?? '';
}

/** The phone the forms open on: the card's first line, else the number that texted it. */
function cardPhone(row: TextedCardOfferRow): string {
  return row.card.phones[0]?.value || row.fromNumber;
}

/** Create Lead's form, pre-filled: source Text message, the sending number as the occasion, and the card's contact. */
export function textedCardLeadValues(row: TextedCardOfferRow): Record<string, unknown> {
  return {
    source: 'TextMessage',
    sourceDetail: textedCardFrom(row.fromNumber),
    submittedName: textedCardName(row.card),
    submittedCompanyName: row.card.organization ?? '',
    submittedEmail: cardEmail(row.card),
    submittedPhone: cardPhone(row),
  };
}

/** Create Person at a Company's form, pre-filled: the name, the company the number or the card resolves to, the work email, the phone and the title. */
export function textedCardPersonValues(row: TextedCardOfferRow): Record<string, unknown> {
  return {
    ...textedCardNameParts(row.card),
    ...(row.company ? { companyId: row.company.id, companyIdLabel: row.company.displayName } : {}),
    workEmail: cardEmail(row.card),
    workPhone: cardPhone(row),
    ...(row.card.title ? { title: row.card.title } : {}),
  };
}

/**
 * 🔒 The fields Create Person at a Company adds to Person's own (decision 5180): the
 * company the employment is at, its work email, its work phone and a title. 🔨 The company
 * is required, as a message's sender's is (decision 1506); the email is not, because a
 * texted card often carries a phone alone, and the create asks for one of the two.
 */
export const TEXTED_CARD_PERSON_FIELDS: readonly EditField[] = [
  { key: 'companyId', label: 'Company', kind: 'company', required: true },
  { key: 'workEmail', label: 'Work email', kind: 'email' },
  { key: 'workPhone', label: 'Work phone', kind: 'phone' },
  { key: 'title', label: 'Title', kind: 'text' },
];
